import { useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "../../../context";

const catColors = {
  Recipes: "bg-orange-100 text-orange-600", Culture: "bg-blue-100 text-blue-600",
  Health: "bg-green-100 text-green-600", Beverages: "bg-amber-100 text-amber-700",
  News: "bg-purple-100 text-purple-600", Community: "bg-pink-100 text-pink-600",
};

export const BlogDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { blogPosts } = useAppContext();

  const post = blogPosts.find(b => String(b.id) === String(id));
  const related = blogPosts.filter(b => b.status === "Published" && String(b.id) !== String(id)).slice(0, 3);

  if (blogPosts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center text-gray-400">
        Loading...
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <p className="text-gray-400 mb-4">Post not found.</p>
        <button onClick={() => navigate("/blog")} className="text-[#f97316] font-bold hover:underline">← Back to Blog</button>
      </div>
    );
  }

  const readTime = post.read || post.read_time;
  const tags = Array.isArray(post.tags) ? post.tags : [];
  const date = post.created_at
    ? new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <button onClick={() => navigate("/blog")} className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#f97316] mb-8 transition-colors">
        ← Back to Blog
      </button>

      <span className={`text-xs font-bold px-3 py-1.5 rounded-full mb-4 inline-block ${catColors[post.cat] || "bg-gray-100 text-gray-600"}`}>
        {post.cat}
      </span>

      <h1 className="font-serif text-4xl md:text-5xl text-gray-900 leading-tight mb-5">{post.title}</h1>

      <div className="flex items-center gap-4 text-sm text-gray-400 mb-8">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-[#f97316] to-red-500 rounded-xl flex items-center justify-center text-white text-xs font-black">
            {(post.author || "?").split(" ").map(w => w[0]).join("")}
          </div>
          <span className="font-medium text-gray-700">{post.author}</span>
        </div>
        {date && <><span>·</span><span>📅 {date}</span></>}
        {readTime && <><span>·</span><span>⏱ {readTime} read</span></>}
      </div>

      {post.img && (
        <div className="rounded-3xl overflow-hidden mb-10 shadow-xl img-zoom">
          <img src={post.img} alt={post.title} className="w-full h-80 object-cover" />
        </div>
      )}

      <div className="prose max-w-none text-gray-600 leading-relaxed space-y-5">
        {post.excerpt && (
          <p className="text-xl font-medium text-gray-700 leading-relaxed">{post.excerpt}</p>
        )}
        {post.content && (
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        )}
      </div>

      {tags.length > 0 && (
        <div className="flex gap-2 mt-8 mb-12 flex-wrap">
          {tags.map(t => (
            <span key={t} className="text-xs bg-orange-50 text-orange-600 px-3 py-1.5 rounded-full font-medium">#{t}</span>
          ))}
        </div>
      )}

      {related.length > 0 && (
        <div className="border-t border-gray-100 pt-12">
          <h3 className="font-serif text-2xl text-gray-900 mb-6">More Articles</h3>
          <div className="grid md:grid-cols-3 gap-5">
            {related.map(b => (
              <button key={b.id} onClick={() => navigate(`/blog/${b.id}`)} className="bg-white rounded-2xl overflow-hidden border border-gray-100 card-hover group text-left img-zoom block w-full">
                <div className="overflow-hidden">
                  {b.img
                    ? <img src={b.img} alt={b.title} className="w-full h-36 object-cover" />
                    : <div className="w-full h-36 bg-orange-50 flex items-center justify-center text-3xl">📝</div>
                  }
                </div>
                <div className="p-4">
                  <h4 className="font-bold text-sm text-gray-900 lc2 group-hover:text-[#f97316] transition-colors">{b.title}</h4>
                  <p className="text-xs text-gray-400 mt-1">{b.author} · {b.read || b.read_time} read</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
